import React, { useEffect, useState } from 'react';
import './image-resize-overlay.scss';

interface ImageResizeOverlayProps {
  image: HTMLImageElement;
  onClose: () => void;
}

export const ImageResizeOverlay: React.FC<ImageResizeOverlayProps> = ({
  image,
  onClose,
}) => {
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  const handleResizeStart = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();

    setResizeHandle(handle);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
      width: image.offsetWidth,
      height: image.offsetHeight,
    });
  };

  useEffect(() => {
    if (!resizeHandle || !dragStart) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      let newWidth = dragStart.width;
      let newHeight = dragStart.height;

      if (resizeHandle.includes('e')) {
        newWidth = Math.max(50, dragStart.width + deltaX);
      }
      if (resizeHandle.includes('w')) {
        newWidth = Math.max(50, dragStart.width - deltaX);
      }
      if (resizeHandle.includes('s')) {
        newHeight = Math.max(50, dragStart.height + deltaY);
      }
      if (resizeHandle.includes('n')) {
        newHeight = Math.max(50, dragStart.height - deltaY);
      }

      // Maintain aspect ratio
      const aspectRatio = dragStart.width / dragStart.height;
      if (
        resizeHandle === 'se' ||
        resizeHandle === 'sw' ||
        resizeHandle === 'ne' ||
        resizeHandle === 'nw'
      ) {
        newHeight = newWidth / aspectRatio;
      }

      image.style.width = `${newWidth}px`;
      image.style.height = 'auto';
    };

    const handleMouseUp = () => {
      setResizeHandle(null);
      setDragStart(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeHandle, dragStart, image]);

  const handleAlignmentChange = (alignment: string) => {
    image.style.display = 'block';
    image.style.marginLeft =
      alignment === 'left' ? '0' : alignment === 'center' ? 'auto' : 'auto';
    image.style.marginRight =
      alignment === 'left' ? 'auto' : alignment === 'center' ? 'auto' : '0';
  };

  return (
    <div
      className='image-resize-overlay'
      style={{
        left: image.offsetLeft,
        top: image.offsetTop,
        width: image.offsetWidth,
        height: image.offsetHeight,
      }}
    >
      {/* Resize handles */}
      {['nw', 'ne', 'sw', 'se'].map((handle) => (
        <div
          key={handle}
          className={`resize-handle resize-handle-${handle}`}
          onMouseDown={(e) => handleResizeStart(e, handle)}
        />
      ))}

      {/* Alignment toolbar */}
      <div className='image-alignment-toolbar'>
        <button
          onClick={() => handleAlignmentChange('left')}
          title='Align left'
        >
          ←
        </button>
        <button onClick={() => handleAlignmentChange('center')} title='Center'>
          ↔
        </button>
        <button
          onClick={() => handleAlignmentChange('right')}
          title='Align right'
        >
          →
        </button>
      </div>
    </div>
  );
};
